import { useHydraUIAPIQuery } from '@/services/api';
import { getWorkspaceSettings, workspaceSettingsKeys } from '@/services/db/environment/queries';

export const DEFAULT_MAX_COLLECTION_COUNT = 50;
// 10 MB decimal, matching the Ember app's constant (ui/app/utils/collections.ts) — the
// value a workspace without an override falls back to. Deliberately not 10 MiB.
export const DEFAULT_MAX_COLLECTION_BYTES = 10 * 1000 * 1000;

export type CollectionWorkspaceLimits = {
  /**
   * `undefined` until the workspace settings resolve — while the request is in flight or
   * after it fails. Consumers must not restrict on an unknown limit: guessing one risks
   * blocking something the API would have allowed, and the server enforces both limits
   * regardless, so the only cost of waiting is a late client-side check.
   */
  maxCollectionCount: number | undefined;
  maxFileSizeBytes: number | undefined;
};

/**
 * Both settings are optional and nullable in the API, and a non-positive value is not a
 * usable limit — a workspace that may hold zero collections, or accepts only zero-byte
 * files, would render a permanently disabled Add collection button and reject every
 * upload. Anything at or below zero is therefore treated as "no override" and falls back
 * to the default rather than being taken literally.
 */
function resolveLimit(value: number | null | undefined, fallback: number): number {
  return value != null && value > 0 ? value : fallback;
}

/** @internal Exported for testing. */
export function resolveCollectionWorkspaceLimits(
  settings: { max_collection_size?: number | null; max_collection_bytes?: number | null } | undefined,
): CollectionWorkspaceLimits {
  // Settings we don't have yet are not the same as settings that carry no override: only
  // the latter can stand in a default, because only then do we know none was configured.
  if (settings == null) {
    return { maxCollectionCount: undefined, maxFileSizeBytes: undefined };
  }

  return {
    maxCollectionCount: resolveLimit(settings.max_collection_size, DEFAULT_MAX_COLLECTION_COUNT),
    maxFileSizeBytes: resolveLimit(settings.max_collection_bytes, DEFAULT_MAX_COLLECTION_BYTES),
  };
}

export function useCollectionWorkspaceLimits(workspaceId: number): CollectionWorkspaceLimits {
  const query = useHydraUIAPIQuery({
    queryKey: workspaceSettingsKeys.detail(workspaceId),
    queryFn: ({ signal }) => getWorkspaceSettings(workspaceId, signal),
    queryConfig: {
      enabled: workspaceId > 0,
      staleTime: 300_000,
      refetchOnWindowFocus: false,
    },
  });

  return resolveCollectionWorkspaceLimits(query.data?.settings);
}
